// ---------------------------------------------------------------------------
// DiCharts – OHLC Data Store
// ---------------------------------------------------------------------------
// Maintains the CPU‑side array of candle data.  Provides efficient
// append, update‑last, and trim operations for streaming use.
// ---------------------------------------------------------------------------

import type { CandleDataPoint, OHLCTuple } from '../types';

function asOHLC(p: CandleDataPoint): OHLCTuple {
  if (Array.isArray(p)) return p as unknown as OHLCTuple;
  const o = p as import('../types').OHLCObject;
  return [o.timestamp, o.open, o.high, o.low, o.close];
}

export interface OHLCStore {
  /** All candle data. */
  readonly data: OHLCTuple[];
  /** Replace all data. */
  setData(candles: CandleDataPoint[]): void;
  /** Append new candles to the end. */
  append(candles: CandleDataPoint[]): void;
  /** Update (replace) the last candle – used for the "forming" candle. */
  updateLast(candle: CandleDataPoint): void;
  /** Trim the store so it holds at most `maxCandles` entries (removes oldest). */
  trim(maxCandles: number): void;
  /** Number of candles. */
  readonly length: number;
}

export function createOHLCStore(initial?: CandleDataPoint[]): OHLCStore {
  let _data: OHLCTuple[] = initial ? initial.map(asOHLC) : [];

  return {
    get data() { return _data; },
    get length() { return _data.length; },

    setData(candles: CandleDataPoint[]) {
      _data = candles.map(asOHLC);
    },

    append(candles: CandleDataPoint[]) {
      for (const c of candles) {
        _data.push(asOHLC(c));
      }
    },

    updateLast(candle: CandleDataPoint) {
      if (_data.length === 0) {
        _data.push(asOHLC(candle));
      } else {
        _data[_data.length - 1] = asOHLC(candle);
      }
    },

    trim(maxCandles: number) {
      if (_data.length > maxCandles) {
        _data = _data.slice(_data.length - maxCandles);
      }
    },
  };
}
